import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppSelect from '@nxsys/components/AppSelect';
import AppUploadFile from '@nxsys/components/AppUploadFile';
import { MASTER_TEMPLATE_FILE_PATH, downloadFile } from '@nxsys/constants/templateFiles';
import { Form, Input } from 'antd';
import { FormProps } from 'antd/lib';
import React, { useEffect, useState } from 'react';
import {
  AGENCY_UPLOAD_FILE_TYPE,
  agencyUploadTypeOptions,
} from '../../import/constants';
import { Option } from '@nxsys/types/models/Common';
import { AppState } from '@nxsys/store';
import { useSelector } from 'react-redux';
import { IRELAND } from '@nxsys/store/constants/keys';

type Props = {
  isOpen: boolean;
  toggle: () => void;
  onSubmit: (values: AgencyUploadValues) => Promise<void>;
  defaultValues?: Partial<AgencyUploadValues>;
};

export type AgencyUploadValues = {
  fileType: Option<string> | null;
  notes: string;
  documentFile: File;
};

const AgencyUploadModal: React.FC<Props> = ({
  isOpen,
  toggle,
  onSubmit,
  defaultValues = {},
}) => {
  const [form] = Form.useForm();
  const watchValues = Form.useWatch([], form);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;

  useEffect(() => {
    if (isOpen) {
      form.setFieldsValue(defaultValues);
    }
  }, [isOpen, defaultValues]);

  const onFinish: FormProps['onFinish'] = (values) => {
    setIsSubmitting(true);
    onSubmit(values)
      .then(() => {
        form.resetFields();
        toggle();
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  const handleDownloadTemplate = () => {
    if (watchValues.fileType?.value === AGENCY_UPLOAD_FILE_TYPE.PROSPECTS) {
      const link = !isBureau
        ? (isIreland
          ? MASTER_TEMPLATE_FILE_PATH.umbrellaIreland.importProspect
          : MASTER_TEMPLATE_FILE_PATH.umbrella.importProspect)
        : isIreland
          ? MASTER_TEMPLATE_FILE_PATH.ireland.importProspect
          : MASTER_TEMPLATE_FILE_PATH.uk.importProspect;
      downloadFile(link);
    } else if (
      watchValues.fileType?.value === AGENCY_UPLOAD_FILE_TYPE.TIMESHEETS
    ) {
      const link = !isBureau
        ? (isIreland
          ? MASTER_TEMPLATE_FILE_PATH.umbrellaIreland.importTimesheet
          : MASTER_TEMPLATE_FILE_PATH.umbrella.importTimesheet)
        : isIreland
          ? MASTER_TEMPLATE_FILE_PATH.ireland.importTimesheet
          : MASTER_TEMPLATE_FILE_PATH.uk.importTimesheet;
      downloadFile(link);
    } else if (
      watchValues.fileType?.value === AGENCY_UPLOAD_FILE_TYPE.SUBCONTRACTORS
    ) {
      const link = !isBureau
        ? (isIreland
          ? MASTER_TEMPLATE_FILE_PATH.umbrellaIreland.importEmployee
          : MASTER_TEMPLATE_FILE_PATH.umbrella.importEmployee)
        : isIreland
          ? MASTER_TEMPLATE_FILE_PATH.ireland.importEmployee
          : MASTER_TEMPLATE_FILE_PATH.uk.importEmployee;
      downloadFile(link);
    }
  };

  return (
    <AppModalContainer
      title="File Upload"
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: 'Download Template',
          variant: 'outlined',
          onClick: handleDownloadTemplate,
          disabled: !watchValues?.fileType,
        },
        {
          name: 'Cancel',
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: 'Save',
          buttonType: 'primary',
          onClick: () => form.submit(),
          loading: isSubmitting,
          disabled: isSubmitting,
        },
      ]}
      minHeight="200px"
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        <Form.Item
          label="File Type"
          name="fileType"
          rules={[
            {
              required: true,
              message: 'Please select file type',
            },
          ]}
        >
          <AppSelect
            options={agencyUploadTypeOptions}
            placeholder="Select file type"
          />
        </Form.Item>
        <Form.Item label="Notes for the Payroll" name="notes">
          <Input.TextArea
            placeholder="Notes for the Payroll"
            rows={4}
            onChange={(e) => {
              form.setFieldsValue({ notes: e.target.value });
            }}
          />
        </Form.Item>
        <Form.Item
          label="Attach Document"
          name="documentFile"
          rules={[
            {
              required: true,
              message: 'Please attach document',
            },
          ]}
        >
          <AppUploadFile
            onChange={(file: File) => {
              form.setFieldsValue({ documentFile: file });
            }}
          />
        </Form.Item>
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default AgencyUploadModal;
